// <popup-banner> — behaviour layer for the `siclips-popup-banner` section.
//
// The section renders a native <dialog> (opened with showModal(): browser top layer, free focus
// trap + aria-modal + inert background + focus return). This element decides WHEN to open it
// (delay per breakpoint + a frequency gate in local/sessionStorage), locks/restores body scroll
// (the dialog.js mechanics), and runs the ANIMATED close: add .spb--closing, wait for the dialog's
// opacity transition to end (safety timeout as backup), then dialog.close(). Every close path
// (X button, dismiss link, overlay click, ESC via the `cancel` event) records the frequency stamp.
// Storage is fully try/catch'd — Safari private mode behaves as "always". In the theme editor
// (Shopify.designMode) the popup opens immediately, never writes storage, and follows
// shopify:section:select / deselect for previewing.

const DAY_MS = 86400000;

class PopupBanner extends HTMLElement {
  connectedCallback() {
    // Element-level setup binds ONCE per element instance: the theme editor MOVES the existing
    // node on drag-reorder (disconnect + reconnect of the SAME element) and its listeners
    // survive the move — rebinding here would stack duplicate handlers. A settings re-render
    // creates a FRESH element, which binds normally.
    if (!this._boundEl) {
      this._boundEl = true;
      this.cfg = this._readJSON('[data-spb-config]') || {};
      this.dialog = this.querySelector('dialog.spb');
      this.card = this.querySelector('.spb__card');
      this.sectionId = this.cfg.sectionId || this.getAttribute('data-section-id') || '';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._closing = false;
      this._locked = false;
      this._prevScrollY = 0;
      if (this.dialog && this.card) this._bindDialog();
    }
    if (!this.dialog || !this.card) return;

    if (this.designMode) {
      // document listeners are removed on disconnect — rebind on every connect
      if (!this._docBound) {
        this._docBound = true;
        this._onSelect = (e) => {
          if (this._isThisSection(e)) this.open();
        };
        this._onDeselect = (e) => {
          if (this._isThisSection(e)) this.close();
        };
        document.addEventListener('shopify:section:select', this._onSelect);
        document.addEventListener('shopify:section:deselect', this._onDeselect);
      }
      // preview auto-open: once per element, NEVER for a disabled popup (the owner must be able
      // to preview the site with it off — selecting the section still opens it for editing).
      // start-minimized: preview the true first paint (chip only); selecting still opens.
      if (this.cfg.enabled && !this.cfg.startMinimized && !this._previewOpened) {
        this._previewOpened = true;
        this.open();
      }
      if (this.cfg.minimizeEnable) this._showChip(); // stylable in the editor
      return;
    }

    if (this._scheduled) return; // storefront: schedule once per element
    this._scheduled = true;
    if (!this.cfg.enabled) return;

    // signup success redirect: the user MUST see their coupon — open now, bypass the
    // frequency gate for this one open (closing still stamps it), and strip the marker
    // param so a refresh doesn't re-trigger
    if (this._signupPosted) {
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has('customer_posted')) {
          u.searchParams.delete('customer_posted');
          window.history.replaceState(window.history.state, '', u.toString());
        }
      } catch (e) {}
      this._timer = setTimeout(() => this.open(), 250);
      return;
    }

    // minimized-chip takeover: a previously-minimized popup (or a signed-up session)
    // shows the chip instead of auto-opening
    if (this._chipRestore()) return;

    if (!this._allowedByFrequency()) return;

    // start-minimized mode (owner option): never auto-open — show only the chip from the
    // start; the popup opens exclusively on a chip click. Dismissals (CTA / chip ✕) still
    // stamp the frequency gate above, so a dismissed popup shows nothing at all.
    if (this.cfg.startMinimized && this.cfg.minimizeEnable) {
      this._showChip();
      return;
    }

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const delay = Math.max(0, +(mobile ? this.cfg.delayMobile : this.cfg.delayDesktop) || 0);
    this._timer = setTimeout(() => this.open(), delay);
  }

  _bindDialog() {
    // ESC → intercept the native cancel and run the ANIMATED close (which also records frequency)
    this._onCancel = (e) => {
      e.preventDefault();
      this.close();
    };
    this.dialog.addEventListener('cancel', this._onCancel);

    // click outside the card (the dialog itself is the full-viewport overlay) → close
    this._onDialogClick = (e) => {
      if (this.cfg.closeOnOverlay === false) return;
      if (e.target instanceof Element && e.target.closest('.spb__card')) return;
      this.close();
    };
    this.dialog.addEventListener('click', this._onDialogClick);

    // belt & braces: if the dialog is closed by any path we didn't drive, never leave scroll locked
    this._onDialogClose = () => this._unlock();
    this.dialog.addEventListener('close', this._onDialogClose);

    this.querySelectorAll('[data-spb-close]').forEach((b) => {
      b.addEventListener('click', () => this.close());
    });
    const cta = this.querySelector('[data-spb-cta]');
    if (cta) {
      // CTA link: run the animated close (records the frequency stamp synchronously first, so a
      // navigation can never race it) — also handles same-page anchor links cleanly
      cta.addEventListener('click', () => this.close({ dismiss: true }));
    }

    this._bindChip();
    this._bindSignup();
  }

  // Silently apply the coupon on TWO layers:
  // 1) /discount/CODE — the checkout cookie (works even with an empty cart / other pages);
  // 2) attach the code to the CART itself (the theme's own cart-discount mechanism:
  //    POST cart_update_url with {discount, sections} then morph the drawer section) so the
  //    CART SIDEBAR shows the discount pill + discounted totals immediately.
  // Because the popup usually fires BEFORE anything is in the cart, the code is also remembered
  // (sessionStorage) and re-attached on every later cart add ('cart:update') until it sticks.
  _applyCoupon(wrap) {
    return; // 2026-07-26 owner A/B test: auto-apply DISABLED while investigating the
            // hCaptcha challenge redirects; re-enable by removing this line (see CLAUDE.md)
    if (this._couponApplied) return;
    if (!wrap || !wrap.hasAttribute('data-spb-autoapply') || !window.fetch) return;
    const code = wrap.getAttribute('data-spb-code') || '';
    if (!code) return;
    this._couponApplied = true;
    try { window.sessionStorage.setItem('siclipsSpbCoupon', code); } catch (e) {}
    try {
      fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(() => {});
    } catch (e) {}
    this._attachCouponToCart(code);
    this._watchCartForCoupon();
  }

  _attachCouponToCart(code) {
    if (this._attachBusy) return;
    this._attachBusy = true;
    try {
      const comp = document.querySelector('cart-discount-component');
      const sectionId = comp ? comp.dataset.sectionId : null;
      const existing = Array.from(document.querySelectorAll('.cart-discount__pill'))
        .map((el) => el.dataset.discountCode)
        .filter(Boolean);
      if (existing.indexOf(code) !== -1) { this._attachBusy = false; return; }
      const body = { discount: existing.concat(code).join(',') };
      if (sectionId) body.sections = [sectionId];
      const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((data) => {
          this._attachBusy = false;
          if (!sectionId || !data || !data.sections || !data.sections[sectionId]) return;
          // morph the drawer + notify the theme exactly like assets/cart-discount.js does
          return Promise.all([import('@theme/section-renderer'), import('@theme/events')])
            .then(([sr, ev]) => {
              sr.morphSection(sectionId, data.sections[sectionId]);
              document.dispatchEvent(new ev.DiscountUpdateEvent(data, 'siclips-popup-banner'));
            })
            .catch(() => {});
        })
        .catch(() => { this._attachBusy = false; });
    } catch (e) {
      this._attachBusy = false;
    }
  }

  _watchCartForCoupon() {
    if (this._cartWatchBound) return;
    this._cartWatchBound = true;
    // re-attach after cart changes (the signup usually happens before the cart has items,
    // and Shopify drops codes attached to an empty cart)
    this._onCartUpdate = () => {
      let code = '';
      try { code = window.sessionStorage.getItem('siclipsSpbCoupon') || ''; } catch (e) {}
      if (!code) return;
      const attached = Array.from(document.querySelectorAll('.cart-discount__pill'))
        .some((el) => el.dataset.discountCode === code);
      if (!attached) setTimeout(() => this._attachCouponToCart(code), 350);
    };
    document.addEventListener('cart:update', this._onCartUpdate);
  }

  // ---------- email signup -> coupon ----------
  // Primary path: AJAX-submit the customer form (no reload) and swap the stacked states in
  // place — the wrapper keeps the size of the larger state, so the banner never jumps.
  // Fallback path (no JS / fetch failure): the normal POST redirects back with
  // ?customer_posted=true, Liquid renders [data-spb-posted], and connectedCallback force-opens
  // the popup already in its coupon state.
  _bindSignup() {
    const wrap = this.querySelector('[data-spb-signup]');
    if (!wrap) return;
    this.signupWrap = wrap;
    const form = wrap.querySelector('form');
    const errorEl = wrap.querySelector('[data-spb-error]');
    const submitBtn = wrap.querySelector('[data-spb-submit]');

    // a code remembered from an earlier signup this session -> keep re-attaching on cart adds
    try {
      if (window.sessionStorage.getItem('siclipsSpbCoupon')) this._watchCartForCoupon();
    } catch (e) {}

    // server-rendered success (redirect fallback) -> start in the coupon state
    if (wrap.querySelector('[data-spb-posted]')) {
      wrap.setAttribute('data-spb-signup-state', 'coupon');
      this._signupPosted = true;
      this._applyCoupon(wrap);
    }

    if (form) {
      // 2026-07-26 owner A/B test: the AJAX submit layer is REMOVED — a fetch() POST to
      // /contact lacks the navigation trust signals of a real form submit (sec-fetch-mode:
      // navigate + user activation), which is the prime suspect for Shopify's bot-challenge
      // redirects. The form now always submits NATIVELY with a busy state; the redirect
      // returns with customer_posted=true and the popup reopens in its coupon state.
      form.addEventListener('submit', (e) => {
        if (this.designMode) { e.preventDefault(); return; } // never create customers from the editor
        if (errorEl) errorEl.classList.remove('is-on');
        if (submitBtn && !this._signupBusy) {
          this._signupBusy = true;
          submitBtn.setAttribute('aria-busy', 'true');
          submitBtn.style.pointerEvents = 'none';
          this._busyRestoreText = submitBtn.textContent;
          submitBtn.textContent = submitBtn.getAttribute('data-spb-busy-text') || this._busyRestoreText;
          clearTimeout(this._busyT);
          this._busyT = setTimeout(() => {
            this._signupBusy = false;
            submitBtn.removeAttribute('aria-busy');
            submitBtn.style.pointerEvents = '';
            submitBtn.textContent = this._busyRestoreText;
          }, 8000);
        }
        // no preventDefault — the browser drives the submit
      });
    }

    const copy = wrap.querySelector('[data-spb-copy]');
    if (copy) {
      copy.addEventListener('click', () => {
        const code = copy.getAttribute('data-spb-code') || '';
        const copied = copy.getAttribute('data-spb-copied-text') || 'הועתק!';
        const original = copy.textContent;
        const flash = () => {
          copy.textContent = copied;
          clearTimeout(this._copyT);
          this._copyT = setTimeout(() => { copy.textContent = original; }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(flash, flash);
        } else {
          flash();
        }
      });
    }
  }

  disconnectedCallback() {
    this._docBound = false;
    if (this._timer) clearTimeout(this._timer);
    if (this._closeT) clearTimeout(this._closeT);
    if (this._copyT) clearTimeout(this._copyT);
    if (this._busyT) clearTimeout(this._busyT);
    this._timer = this._closeT = this._copyT = this._busyT = 0;
    if (this._onSelect) document.removeEventListener('shopify:section:select', this._onSelect);
    if (this._onDeselect) document.removeEventListener('shopify:section:deselect', this._onDeselect);
    this._onSelect = this._onDeselect = null;
    // never leave the page scroll-locked behind a torn-down dialog
    this._closing = false;
    this._unlock();
    if (this.dialog && this.dialog.open) {
      try { this.dialog.close(); } catch (err) { /* noop */ }
    }
  }

  // ---------- open / close ----------
  open() {
    const d = this.dialog;
    if (!d || d.open || this._closing || !this.isConnected) return;
    // a closed dialog is display:none, so lazy images never intersected — load them now
    d.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    const y = window.scrollY;
    this._prevScrollY = y;
    // separate DOM reads from writes (dialog.js pattern)
    requestAnimationFrame(() => {
      if (!this.isConnected || d.open || this._closing) return;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${y}px`;
      this._locked = true;
      try {
        d.showModal();
      } catch (err) {
        this._unlock();
        return;
      }
      this.card.focus({ preventScroll: true });
    });
  }

  // opts.dismiss forces the REAL dismissal (frequency stamp) even in minimize mode —
  // used by the chip's ✕ and by CTA clicks (the visitor acted). Every other close path
  // MINIMIZES to the chip when minimize_enable is on.
  close(opts) {
    const d = this.dialog;
    if (!d || !d.open || this._closing) return;
    this._closing = true;
    const minimize = !!(this.cfg && this.cfg.minimizeEnable) && !(opts && opts.dismiss);
    if (minimize) {
      this._rememberState('minimized');
    } else {
      this._remember();
      this._clearState();
      this._hideChip();
    }

    const finish = () => {
      if (!this._closing) return;
      this._closing = false;
      if (this._closeT) clearTimeout(this._closeT);
      this._closeT = 0;
      d.removeEventListener('transitionend', onEnd);
      d.classList.remove('spb--closing');
      this._unlock();
      try { d.close(); } catch (err) { /* noop */ }
      if (minimize) this._showChip();
    };
    const onEnd = (e) => {
      if (e.target === d && e.propertyName === 'opacity') finish();
    };

    d.addEventListener('transitionend', onEnd);
    d.classList.add('spb--closing');
    const dur = this.rmq.matches ? 150 : 180;
    this._closeT = setTimeout(finish, dur + 140); // safety net if transitionend never fires
  }

  _unlock() {
    if (!this._locked) return;
    this._locked = false;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this._prevScrollY, behavior: 'instant' });
  }

  // ---------- minimized chip ("gift tab") ----------
  _chipEls() {
    return {
      wrap: this.querySelector('[data-spb-chip-wrap]'),
      btn: this.querySelector('[data-spb-chip]'),
      label: this.querySelector('[data-spb-chip-label]'),
      x: this.querySelector('[data-spb-chip-x]'),
    };
  }

  _bindChip() {
    const c = this._chipEls();
    if (!c.wrap || this._chipBound) return;
    this._chipBound = true;
    if (c.btn) {
      c.btn.addEventListener('click', () => {
        this._hideChip();
        this.open();
      });
    }
    if (c.x) {
      c.x.addEventListener('click', (e) => {
        e.stopPropagation();
        this._hideChip();
        this._remember();      // the chip's ✕ IS the real dismissal
        this._clearState();
      });
    }
  }

  _showChip() {
    const c = this._chipEls();
    if (!c.wrap) return;
    if (c.label && c.btn) {
      // after a signup this session, the chip becomes the coupon re-access point
      let hasCoupon = false;
      try { hasCoupon = !!window.sessionStorage.getItem('siclipsSpbCoupon'); } catch (e) {}
      const posted = this._signupPosted || hasCoupon;
      c.label.textContent = posted
        ? c.btn.getAttribute('data-spb-chip-coupon-text') || c.label.textContent
        : c.btn.getAttribute('data-spb-chip-text') || c.label.textContent;
    }
    c.wrap.hidden = false;
  }

  _hideChip() {
    const c = this._chipEls();
    if (c.wrap) c.wrap.hidden = true;
  }

  // returns true when the chip took over (popup must NOT auto-open)
  _chipRestore() {
    if (!this.cfg.minimizeEnable) return false;
    let state = '';
    try {
      const raw = this._store().getItem(this._storageKey());
      const data = raw ? JSON.parse(raw) : null;
      state = (data && data.state) || '';
    } catch (e) {}
    let hasCoupon = false;
    try { hasCoupon = !!window.sessionStorage.getItem('siclipsSpbCoupon'); } catch (e) {}
    if (state === 'minimized' || hasCoupon) {
      this._showChip();
      // if the chip is hidden on THIS breakpoint (show d/m toggles), it cannot take over —
      // fall back to the normal popup flow instead of leaving the visitor with nothing
      const c = this._chipEls();
      if (c.wrap && window.getComputedStyle(c.wrap).display === 'none') {
        c.wrap.hidden = true;
        return false;
      }
      return true;
    }
    return false;
  }

  _rememberState(state) {
    if (this.designMode) return;
    try {
      const key = this._storageKey();
      let data = null;
      try { data = JSON.parse(this._store().getItem(key)); } catch (e) {}
      if (!data || typeof data !== 'object') data = { v: 1 };
      data.state = state;
      this._store().setItem(key, JSON.stringify(data));
    } catch (err) { /* storage unavailable */ }
  }

  _clearState() {
    if (this.designMode) return;
    try {
      const key = this._storageKey();
      const data = JSON.parse(this._store().getItem(key));
      if (data && data.state) {
        delete data.state;
        this._store().setItem(key, JSON.stringify(data));
      }
    } catch (err) { /* noop */ }
  }

  // ---------- frequency gate ----------
  _storageKey() {
    return `siclipsPopupBanner:${this.sectionId}`;
  }

  _store() {
    return (this.cfg.frequency || 'days') === 'session' ? window.sessionStorage : window.localStorage;
  }

  _allowedByFrequency() {
    const mode = this.cfg.frequency || 'days';
    if (mode === 'always') return true;
    try {
      const raw = this._store().getItem(this._storageKey());
      if (!raw) return true;
      const data = JSON.parse(raw);
      const closedAt = data && +data.closedAt;
      if (!closedAt) return true;
      if (mode === 'session') return false;
      const days = Math.max(1, +this.cfg.frequencyDays || 7);
      return Date.now() - closedAt > days * DAY_MS;
    } catch (err) {
      return true; // storage unavailable (Safari private mode) → behave as "always"
    }
  }

  _remember() {
    if (this.designMode) return; // NEVER write storage from the theme editor
    const mode = this.cfg.frequency || 'days';
    if (mode === 'always') return;
    try {
      this._store().setItem(this._storageKey(), JSON.stringify({ v: 1, closedAt: Date.now() }));
    } catch (err) {
      /* storage unavailable → behave as "always" */
    }
  }

  // ---------- helpers ----------
  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  _isThisSection(e) {
    const id = e && e.detail && e.detail.sectionId;
    if (id != null && String(id) === String(this.sectionId)) return true;
    return !!(e && e.target instanceof Element && e.target.contains(this));
  }
}

if (!customElements.get('popup-banner')) {
  customElements.define('popup-banner', PopupBanner);
}
